import React, { useEffect, useState, useMemo } from 'react';
import { Modal } from '@mui/material';
import { toast } from 'react-toastify';
import { assignJobToCandidate } from '../../../../../api/sales';
import axiosInstance from '../../../../../api/axiosInstance';
import MyTable from '../../../../../components/MyTable';

const AssignJobFromCandidateModal = ({
  isOpen,
  onClose,
  candidateId,
  candidateSalesPersonId,
  onSaveSuccess,
}) => {

  const [jobs, setJobs] = useState([]);
  const [isLoadingJobs, setIsLoadingJobs] = useState(false);
  const [selectedRowIndices, setSelectedRowIndices] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch active master jobs when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchJobs();
      setSelectedRowIndices([]);
      setSearchTerm('');
    }
  }, [isOpen]);

  const fetchJobs = async () => {
    setIsLoadingJobs(true);
    try {
      const res = await axiosInstance.get('/jobs', {
        params: {
          candidateId: candidateId,
          options: 'callFrom=Candidate_Marketing_Jobs',
          status: 'Active',
        },
      });
      const allJobs = res.data.data.records || [];
      setJobs(allJobs);
    } catch (error) {
      toast.error('Failed to fetch jobs');
      setJobs([]);
    } finally {
      setIsLoadingJobs(false);
    }
  };

  // Apply search filter
  const filteredJobs = useMemo(() => {
    if (!searchTerm) return jobs;
    const searchLower = searchTerm.toLowerCase();
    return jobs.filter(
      (j) =>
        (j.job_title || '').toLowerCase().includes(searchLower) ||
        (j.vendor_name || '').toLowerCase().includes(searchLower) ||
        (j.client || '').toLowerCase().includes(searchLower) ||
        (j.city || '').toLowerCase().includes(searchLower) ||
        (j.sales_person_name || '').toLowerCase().includes(searchLower)
    );
  }, [jobs, searchTerm]);

  const columns = [
    { name: 'job_id', label: 'ID', options: { display: false } },
    {
      name: 'job_title',
      label: 'Job Title',
      options: {
        customBodyRender: (value) => (
          <span className="font-semibold text-secondary-800">{value}</span>
        ),
      },
    },
    { name: 'vendor_name', label: 'Vendor Name' },
    { name: 'client', label: 'Client' },
    {
      name: 'duration',
      label: 'Duration',
      options: {
        customBodyRender: (value) => (value ? `${value} months` : 'N/A'),
      },
    },
    {
      name: 'bill_rate',
      label: 'Bill Rate',
      options: {
        customBodyRender: (value) => (value ? `${value} USD` : 'N/A'),
      },
    },
    { name: 'sales_person_name', label: 'Sales Person' },
    { name: 'city', label: 'Location' },
    {
      name: 'status',
      label: 'Status',
      options: {
        customBodyRender: (value) => (
          <p className="py-1 rounded-full text-center text-secondary-500 text-sm">
            {value || 'N/A'}
          </p>
        ),
      },
    },
  ];

  const tableOptions = {
    filterType: 'dropdown',
    responsive: 'standard',
    selectableRows: 'multiple',
    selectableRowsOnClick: true,
    rowsSelected: selectedRowIndices,
    onRowSelectionChange: (currentRowsSelected, allRowsSelected, rowsSelected) => {
      setSelectedRowIndices(rowsSelected);
    },
    elevation: 0,
    filter: false,
    search: false,
    download: false,
    print: false,
    viewColumns: false,
    rowsPerPage: 10,
    rowsPerPageOptions: [5, 10, 25],
    textLabels: {
      body: {
        noMatch: isLoadingJobs ? '' : 'No active jobs found',
      },
    },
  };

  // Get selected jobs from the filtered list
  const getSelectedJobs = () => {
    return selectedRowIndices
      .map((index) => filteredJobs[index])
      .filter((j) => j?.job_id);
  };

  const handleSave = async () => {
    const selectedJobs = getSelectedJobs();
    if (selectedJobs.length === 0) return;

    setIsSaving(true);
    let successCount = 0;
    let failCount = 0;
    let lastError = '';

    for (const job of selectedJobs) {
      try {
        await assignJobToCandidate(job, candidateId, job.job_id, candidateSalesPersonId);
        successCount++;
      } catch (error) {
        const errMsg =
          error?.response?.data?.details ||
          error?.response?.data?.message ||
          error.message;
        console.error('Assign job failed for job', job.job_id, errMsg);
        lastError = errMsg;
        failCount++;
      }
    }

    setIsSaving(false);

    if (failCount > 0) {
      toast.error(lastError || `Failed to assign ${failCount} job(s)`);
    }
    if (successCount > 0) {
      toast.success(`${successCount} job(s) assigned successfully`);
      onClose();
      setTimeout(() => onSaveSuccess(), 100);
    }
  };

  const selectedCount = selectedRowIndices.length;
  const canSave = selectedCount > 0 && !isSaving;

  return (
    <Modal open={isOpen} onClose={onClose}>
      <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 z-50">
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full md:w-[900px] max-w-4xl max-h-[85vh] flex flex-col border border-gray-200 relative">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-2xl font-bold text-secondary-800">Assign Job</h2>
              <p className="text-sm text-text-hint mt-1">
                Select jobs to assign to this candidate
              </p>
            </div>
            <div className="flex items-center gap-3">
              {selectedCount > 0 && (
                <span className="text-sm text-secondary-700 font-medium">
                  {selectedCount} selected
                </span>
              )}
              <button
                onClick={handleSave}
                disabled={!canSave}
                className={`py-2 px-6 rounded-lg shadow transition ${
                  canSave
                    ? 'bg-secondary-700 text-text-light hover:bg-secondary-800 cursor-pointer'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {isSaving ? 'Saving...' : 'Save'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="text-gray-500 hover:text-secondary-800 text-2xl font-bold px-2"
                aria-label="Close"
              >
                &times;
              </button>
            </div>
          </div>

          {/* Search */}
          <div className="mb-4">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setSelectedRowIndices([]);
              }}
              placeholder="Search by job title, vendor, client, location..."
              className="search-input border-radius-10 w-full px-4 py-2 border border-gray-300 rounded-lg"
            />
          </div>

          {/* Table */}
          <div className="flex-1 overflow-y-auto relative">
            <MyTable
              title=""
              data={isLoadingJobs ? [] : filteredJobs}
              columns={columns}
              options={tableOptions}
            />
            {isLoadingJobs && (
              <div
                className="absolute inset-0 flex flex-col items-center justify-center z-20"
                style={{ background: 'rgba(255,255,255,0.9)' }}
              >
                <div className="w-10 h-10 border-4 border-secondary-200 border-t-secondary-700 rounded-full animate-spin mb-3"></div>
                <span className="text-lg text-secondary-700 font-semibold">
                  Loading jobs...
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default AssignJobFromCandidateModal;
